import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HostAppShell } from '../components/HostAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Star, User, Car, Calendar, MessageSquare, 
  TrendingUp, AlertCircle, Sparkles
} from 'lucide-react';

/**
 * HostReviewsPage - View all reviews received by the host
 * Connected to existing reviews system
 */

const StarRating = ({ rating, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} ${
            star <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'
          }`}
        />
      ))}
    </div>
  );
};

const ReviewCard = ({ review }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Card className="border-slate-100">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Renter Photo */}
          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {review.renter?.profile_photo ? (
              <img 
                src={review.renter.profile_photo} 
                alt={review.renter?.full_name} 
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-5 h-5 text-slate-400" />
            )}
          </div>
          
          {/* Review Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium text-slate-900">{review.renter?.full_name || 'Anonymous'}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <StarRating rating={review.rating} size="sm" />
                  <span className="text-xs text-slate-500">{formatDate(review.created_at)}</span>
                </div>
              </div>
              <Badge variant="outline" className="text-xs flex-shrink-0">
                {review.rating}/5
              </Badge>
            </div>
            
            {/* Vehicle Info */}
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
              <Car className="w-3 h-3" />
              <span>{review.vehicle?.name || 'Unknown Vehicle'}</span>
              {review.booking_ref && (
                <>
                  <span>•</span>
                  <span>Ref: {review.booking_ref}</span>
                </>
              )}
            </div>
            
            {/* Comment */}
            {review.comment && (
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                "{review.comment}"
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RatingDistribution = ({ distribution, totalCount }) => {
  const maxCount = Math.max(...Object.values(distribution), 1);
  
  return (
    <div className="space-y-2">
      {[5, 4, 3, 2, 1].map((rating) => {
        const count = distribution[rating] || 0;
        const percentage = totalCount > 0 ? (count / totalCount) * 100 : 0;
        
        return (
          <div key={rating} className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600 w-4">{rating}</span>
            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-400 rounded-full transition-all"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="text-xs text-slate-500 w-8 text-right">{count}</span>
          </div>
        );
      })}
    </div>
  );
};

const HostReviewsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    total_count: 0,
    average_rating: null,
    rating_distribution: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await api.get('/host/reviews');
      setReviews(res.data.reviews || []);
      setStats({
        total_count: res.data.total_count || 0,
        average_rating: res.data.average_rating,
        rating_distribution: res.data.rating_distribution || {}
      });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <HostAppShell title="Reviews" activeId="reviews">
      <div className="max-w-4xl mx-auto space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
          </div>
        ) : (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Average Rating */}
              <Card className="border-slate-100 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Average Rating</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-3xl font-bold text-slate-900">
                          {stats.average_rating ? stats.average_rating.toFixed(1) : '—'}
                        </span>
                        <span className="text-sm text-slate-500">/5</span>
                      </div>
                      <StarRating rating={stats.average_rating || 0} size="md" />
                    </div>
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Total Reviews */}
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Total Reviews</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_count}</p>
                      <p className="text-sm text-slate-500">from renters</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-blue-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Rating Distribution */}
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Distribution</p>
                  <RatingDistribution 
                    distribution={stats.rating_distribution} 
                    totalCount={stats.total_count}
                  />
                </CardContent>
              </Card>
            </div>
            
            {/* Reviews List */}
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                All Reviews ({stats.total_count})
              </h2>
              
              {reviews.length === 0 ? (
                <Card className="border-slate-100">
                  <CardContent className="py-12 text-center">
                    <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-medium text-slate-700 mb-2">No reviews yet</h3>
                    <p className="text-sm text-slate-500">
                      Reviews from renters will appear here after they complete their trips.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <ReviewCard key={review.review_id} review={review} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </HostAppShell>
  );
};

export default HostReviewsPage;
